import React, { useState, useEffect } from 'react';
import { 
  DisplaySession, 
  fetchDisplaySessions, 
  endDisplaySession 
} from '../../services/displayService';

/**
 * DisplaySessionsList Component
 * 
 * Displays and manages active or past sessions for a display
 */

interface DisplaySessionsListProps {
  displayId: string;
  activeOnly?: boolean;
  limit?: number;
  onEndSession?: (sessionId: string) => void;
}

const DisplaySessionsList: React.FC<DisplaySessionsListProps> = ({
  displayId,
  activeOnly = false,
  limit = 10,
  onEndSession
}) => {
  const [sessions, setSessions] = useState<DisplaySession[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalSessions, setTotalSessions] = useState<number>(0);

  // Fetch sessions for the display
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const result = await fetchDisplaySessions(
          displayId, 
          activeOnly, 
          currentPage, 
          limit
        );
        
        setSessions(result.sessions);
        setTotalSessions(result.total);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching sessions:', err);
        setError(err instanceof Error ? err.message : 'Failed to load sessions');
        setLoading(false);
      }
    };
    
    fetchSessions();
  }, [displayId, activeOnly, currentPage, limit]);

  // Handle ending a session
  const handleEndSession = async (sessionId: string) => {
    try {
      await endDisplaySession(displayId, sessionId);
      
      // Update sessions list
      setSessions(prevSessions => 
        prevSessions.map(session => 
          session._id === sessionId 
            ? { ...session, status: 'ended', endTime: new Date().toISOString() } 
            : session
        )
      );
      
      // Call callback if provided
      if (onEndSession) {
        onEndSession(sessionId);
      }
    } catch (err) {
      console.error(`Error ending session ${sessionId}:`, err);
      setError(err instanceof Error ? err.message : 'Failed to end session');
    }
  };

  // Format duration from seconds
  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'N/A';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  // Calculate session duration
  const calculateDuration = (session: DisplaySession): string => {
    if (session.duration) {
      return formatDuration(session.duration);
    }
    
    const start = new Date(session.startTime).getTime();
    const end = session.endTime 
      ? new Date(session.endTime).getTime() 
      : new Date().getTime();
    
    const durationInSeconds = Math.floor((end - start) / 1000);
    return formatDuration(durationInSeconds);
  };

  // Format date for display
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="text-center p-3">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Loading sessions...</span>
        </div>
        <span className="ms-2">Loading sessions...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="alert alert-info" role="alert">
        {activeOnly ? 'No active sessions found.' : 'No sessions found for this display.'}
      </div>
    );
  }

  return (
    <div className="display-sessions-list">
      <div className="table-responsive">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Status</th>
              <th>Start Time</th>
              <th>Duration</th>
              <th>Device</th>
              <th>Note Views</th>
              {!activeOnly && <th>End Time</th>}
              {activeOnly && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sessions.map(session => (
              <tr key={session._id}>
                <td>
                  <span className={`badge bg-${
                    session.status === 'active' ? 'success' : 
                    session.status === 'ended' ? 'secondary' : 'warning'
                  }`}>
                    {session.status}
                  </span>
                </td>
                <td>{formatDate(session.startTime)}</td>
                <td>{calculateDuration(session)}</td>
                <td>
                  {session.clientInfo?.screenWidth && session.clientInfo?.screenHeight ? (
                    <span>{session.clientInfo.screenWidth}x{session.clientInfo.screenHeight}</span>
                  ) : (
                    'Unknown'
                  )}
                </td>
                <td>{session.noteViews || 0}</td>
                {!activeOnly && <td>{formatDate(session.endTime)}</td>}
                {activeOnly && (
                  <td>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleEndSession(session._id)}
                    >
                      End Session
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination controls */}
      {totalSessions > limit && (
        <nav>
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button 
                className="page-link" 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              >
                Previous
              </button>
            </li>
            
            {/* Page numbers */}
            {Array.from({ length: Math.ceil(totalSessions / limit) }, (_, i) => (
              <li 
                key={i} 
                className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}
              >
                <button 
                  className="page-link" 
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              </li>
            ))}
            
            <li className={`page-item ${currentPage === Math.ceil(totalSessions / limit) ? 'disabled' : ''}`}>
              <button 
                className="page-link" 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalSessions / limit)))}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default DisplaySessionsList; 